import React, { useState, useRef, useEffect } from 'react';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import styles from './banner.module.scss';

const CustomCarousel = ({ bannerFlag }) => {
    const [slides, setSlides] = useState([
        { type: 'video', src: require('@/assets/image/banner/bannervideo.mp4'), alt: '视频轮播' },
        { type: 'image', src: require('@/assets/image/banner/头图 临时1.jpg'), alt: '轮播图1' },
        { type: 'image', src: require('@/assets/image/banner/头图 临时2.jpg'), alt: '轮播图2' },
        { type: 'image', src: require('@/assets/image/banner/临时底图3.jpg'), alt: '轮播图3' },
    ]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isDesktop, setIsDesktop] = useState(false);
    const videoRef = useRef(null);
    const intervalRef = useRef(null);
    const touchStartX = useRef(0);
    const touchEndX = useRef(0);
    const touchMoved = useRef(false);
    const userInteracted = useRef(false);

    const isWeixinBrowser = () => /MicroMessenger/i.test(navigator.userAgent);

    useEffect(() => {
        const checkDesktop = () => setIsDesktop(window.innerWidth > 768);
        checkDesktop();
        window.addEventListener('resize', checkDesktop);
        return () => window.removeEventListener('resize', checkDesktop);
    }, []);

    useEffect(() => {
        if (slides.length === 0) return;

        const isVideo = slides[currentIndex]?.type === 'video';
        const video = videoRef.current;

        if (isVideo && video) {
            video.currentTime = 0;

            if (isWeixinBrowser()) {
                tryWeixinPlay();
            } else {
                video.play().catch(() => {});
            }

            video.onended = () => handleNext();
        }

        startAutoPlay();

        return () => clearInterval(intervalRef.current);
        // eslint-disable-next-line
    }, [currentIndex, slides]);

    const tryWeixinPlay = () => {
        if (typeof window.WeixinJSBridge === 'object' && typeof window.WeixinJSBridge.invoke === 'function') {
            window.WeixinJSBridge.invoke('getNetworkType', {}, () => {
                videoRef.current?.play().catch(() => {});
            });
        } else {
            document.addEventListener('WeixinJSBridgeReady', () => {
                videoRef.current?.play().catch(() => {});
            }, false);
        }
    };

    const startAutoPlay = () => {
        clearInterval(intervalRef.current);
        if (slides[currentIndex]?.type !== 'video') {
            intervalRef.current = setInterval(() => {
                handleNext();
            }, 5000);
        }
    };

    const handleUserInteraction = () => {
        if (!userInteracted.current) {
            userInteracted.current = true;
            const video = videoRef.current;
            if (video && slides[currentIndex]?.type === 'video') {
                video.play().catch(() => {});
            }
        }
    };

    const handleNext = () => {
        setCurrentIndex((prev) => (prev + 1) % slides.length);
    };

    const handlePrev = () => {
        setCurrentIndex((prev) => (prev - 1 + slides.length) % slides.length);
    };

    const goToIndex = (index) => {
        setCurrentIndex(index);
    };

    const onTouchStart = (e) => {
        touchStartX.current = e.touches[0].clientX;
        touchMoved.current = false;
        handleUserInteraction();
    };

    const onTouchMove = (e) => {
        touchEndX.current = e.touches[0].clientX;
        touchMoved.current = true;
    };

    const onTouchEnd = () => {
        if (!touchMoved.current) return;

        const distance = touchEndX.current - touchStartX.current;
        const threshold = 50;

        if (distance > threshold) {
            handlePrev();
        } else if (distance < -threshold) {
            handleNext();
        }
    };

    const handleVideoError = (errorIndex) => {
        setSlides((prevSlides) => {
            const updatedSlides = prevSlides.filter((_, i) => i !== errorIndex);
            if (updatedSlides.length === 0) return [];
            if (currentIndex >= updatedSlides.length) {
                setCurrentIndex(0);
            } else if (errorIndex === currentIndex) {
                handleNext();
            }
            return updatedSlides;
        });
    };

    if (!bannerFlag || slides.length === 0) return null;

    return (
        <div
            className={styles['carousel-container']}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            onClick={handleUserInteraction}
        >
            <div
                className={styles['carousel-wrapper']}
                style={{
                    width: `${slides.length * 100}%`,
                    transform: `translateX(-${(100 / slides.length) * currentIndex}%)`,
                }}
            >
                {slides.map((slide, index) => (
                    <div
                        key={index}
                        className={styles['carousel-item']}
                        style={{ width: `${100 / slides.length}%` }}
                    >
                        {slide.type === 'video' ? (
                            <video
                                ref={index === currentIndex ? videoRef : null}
                                muted
                                playsInline
                                autoPlay
                                className={styles['video']}
                                onError={() => handleVideoError(index)}
                                onCanPlay={() => {
                                    const video = videoRef.current;
                                    if (!video) return;
                                    if ((userInteracted.current || !isWeixinBrowser()) && index === currentIndex) {
                                        video.play().catch(() => {});
                                    } else if (isWeixinBrowser() && index === currentIndex) {
                                        tryWeixinPlay();
                                    }
                                }}
                            >
                                <source src={slide.src} type="video/mp4" />
                                您的浏览器不支持视频标签
                            </video>
                        ) : (
                            <img
                                src={slide.src}
                                alt={slide.alt}
                                className={styles['image']}
                                loading={index === currentIndex ? 'eager' : 'lazy'}
                            />
                        )}
                    </div>
                ))}
            </div>

            {isDesktop && (
                <>
                    <button
                        className={`${styles['nav-button']} ${styles['prev-button']}`}
                        onClick={handlePrev}
                        aria-label="上一张"
                    >
                        <LeftOutlined />
                    </button>
                    <button
                        className={`${styles['nav-button']} ${styles['next-button']}`}
                        onClick={handleNext}
                        aria-label="下一张"
                    >
                        <RightOutlined />
                    </button>
                </>
            )}

            <div className={styles['dots']}>
                {slides.map((_, index) => (
                    <span
                        key={index}
                        className={`${styles['dot']} ${index === currentIndex ? styles.activeDot : ''}`}
                        onClick={() => goToIndex(index)}
                    />
                ))}
            </div>
        </div>
    );
};

export default CustomCarousel;
